import api from '../../../lib/Api'
import { push } from 'connected-react-router'
import { addNotification } from '../../notifications'
import { AccessLevel, capitalize } from '../../../lib/util'
import * as actions from './actions'

export const searchLicensesAndHandle = query => {
  return async function(dispatch) {
    dispatch(actions.fetchingLicenses())

    if (!query || !query.key || !query.value) {
      dispatch(actions.fetchingLicensesError('You must provide a query'))
      return
    }

    dispatch(actions.setLicenseSearchQuery(query.key, query.value))
    const params = `${query.key}=${query.value}`

    try {
      const response = await api.get(`/licenses?${params}`)
      if (response.status === 204 || response.data.length === 0) {
        dispatch(actions.fetchingLicensesNotFound())
      } else {
        dispatch(actions.fetchingLicensesSuccess(response.data))
        if (response.data.length === 1)
          dispatch(push(`/css-admin/licenses/${response.data[0].id}`))
      }
    } catch (err) {
      dispatch(actions.fetchingLicensesError(err))
      return false
    }
  }
}

export const getUserLicenses = userId => {
  return async dispatch => {
    dispatch(actions.fetchingUserLicenses())
    if (!userId) {
      dispatch(actions.fetchingUserLicensesError('You must provide a user id'))
      return
    }
    try {
      const response = await api.get(`/licenses?userid=${userId}`)
      dispatch(actions.fetchingUserLicensesSuccess(response.data))
    } catch (err) {
      dispatch(actions.fetchingUserLicensesError(err))
    }
  }
}

export const getLicense = query => {
  return async function(dispatch) {
    dispatch(actions.fetchingLicenses())

    if (!query) {
      dispatch(actions.fetchingLicensesError('You must provide a license id'))
      return
    }
    try {
      const response = await api.get(`/licenses/${query.licenseId}`)

      if (response.status === 204) {
        dispatch(actions.fetchingLicensesNotFound())
      } else {
        dispatch(actions.fetchingLicenseSuccess(response.data))
      }
    } catch (err) {
      dispatch(actions.fetchingLicensesError(err))
      return false
    }
  }
}

export const saveLicenseAndHandle = updateLicenseReq => {
  return async function(dispatch) {
    dispatch(actions.savingLicense())

    try {
      // TODO: there may need to be some updates to license here, as the API takes
      // a license object. Until the ui is updated its hard to know what may need to be modified.
      const response = await api.put(
        `/licenses/${updateLicenseReq.licenseId}`,
        updateLicenseReq,
      )
      dispatch(actions.savingLicenseSuccess(response.data))
      dispatch(
        addNotification({
          color: 'success',
          text: 'Successfully updated license',
        }),
      )
    } catch (err) {
      let errorText = 'Failed to update license'

      const { response } = err || {}
      const { status, data } = response || {}
      const { detail } = data || {}
      const message = typeof data === 'string' ? data : detail

      if (status === 400 && !!message) {
        errorText = message
      }

      dispatch(actions.savingLicenseError(err))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorText,
        }),
      )
    }
  }
}

// TODO: Leaving this logic here as a reference. When the UI updates are done this can be removed
export const sendLicenseUser = ({
  name,
  email,
  licenseId,
  accessLevel,
  onClose,
  usePut = false,
}) => {
  const licenseUserPayload = {
    name,
    email,
    licenseId,
    roles: [capitalize(accessLevel || '')],
    isCssAdmin: true,
  }

  const upperCaseRole = `${accessLevel
    .charAt(0)
    .toUpperCase()}${accessLevel.slice(1)}`

  return async dispatch => {
    const { article = 'a' } = AccessLevel[accessLevel] || {}
    dispatch(actions.sendingLicenseInvite())
    try {
      if (!licenseId)
        throw new Error('License is undefined, cannot invite user.')

      let licenseUserResponse
      // PUT method is used if user already exists and is being updated
      if (!usePut) {
        licenseUserResponse = await api.post(
          '/licenseuserrole',
          licenseUserPayload,
        )
      } else {
        licenseUserResponse = await api.put(
          '/licenseuserrole',
          licenseUserPayload,
        )
      }

      const licenseUser = licenseUserResponse.data
      onClose()
      dispatch(
        addNotification({
          color: 'success',
          text: `You have ${
            licenseUser.status === 'Accepted' ? 'added' : 'invited'
          } ${email} to the license in the role of ${upperCaseRole}.`,
        }),
      )
      dispatch(actions.sendingLicenseInviteSuccess(licenseUser))
    } catch (error) {
      const { response } = error || {}
      const { data, status, statusText } = response || {}
      const { detail = '' } = data || {}
      const errorText =
        // TODO implement a better code to identify specific errors to show or rather do the update in the API
        detail === 'Maximum number of users on license is reached'
          ? `This license has already reached it’s limit of users in the role ${upperCaseRole}`
          : `We weren't able to invite ${email} as ${article} ${upperCaseRole}. ${detail} Please try again later.`
      const customError = {
        status,
        statusText,
        detail: errorText,
      }
      dispatch(actions.sendingLicenseInviteError(customError))
    }
  }
}

export const sendLicenseUsers = ({ emails, licenseId, role }) => {
  return async dispatch => {
    dispatch(actions.sendingLicenseInviteBulk())

    if (!licenseId)
      throw new Error('License is undefined, cannot invite users.')
    try {
      const response = await api.post('/licenseuserrole/bulk', {
        emails,
        role,
        licenseId,
        isCssAdmin: true,
      })
      const { data: results } = response
      const { added, invited } = results

      const newUsers = []

      added.details.forEach(detail => {
        detail.emails.forEach(email => {
          newUsers.push({
            email,
            userName: '',
            roles: [capitalize(role)],
            status: 'Accepted',
            title: '',
            userId: '',
            licenseId,
          })
        })
      })

      invited.details.forEach(detail => {
        detail.emails.forEach(email => {
          newUsers.push({
            email,
            userName: '',
            roles: [capitalize(role)],
            status: 'Pending',
            title: '',
            userId: '',
            licenseId,
          })
        })
      })

      dispatch(
        actions.sendingLicenseInviteBulkSuccess({
          ...results,
          role: capitalize(role),
          newUsers,
        }),
      )
    } catch (error) {
      const customError = {
        status: error.response.status,
        statusText: error.response.statusText,
        detail: error.response.data.detail,
      }
      dispatch(actions.sendingLicenseInviteBulkError(customError))
    }
  }
}

export const resetBulkOperation = () => {
  return async dispatch => {
    dispatch(actions.resetBulkUsersAdd())
  }
}

export const deleteLicenseUser = (email, licenseId, userId) => {
  const deleteLicenseUserPayload = {
    email,
    licenseId,
    IsCssAdmin: true,
  }

  return async dispatch => {
    dispatch(actions.deletingLicenseUser())
    try {
      if (!licenseId)
        throw new Error('License is undefined, cannot remove invite user.')

      await api.delete(`/licenseuserrole`, {
        data: deleteLicenseUserPayload,
      })
      const successMessage = userId
        ? `You've successfully removed user ${email}.`
        : `You've successfully canceled invite for ${email}.`
      dispatch(
        addNotification({
          color: 'success',
          text: successMessage,
        }),
      )
      dispatch(actions.deletingLicenseUserSuccess(licenseId, email))
    } catch (error) {
      const errorMessage = userId
        ? `We weren't able to remove user ${email}. Please try again later.`
        : `We weren't able to cancel invite for ${email}. Please try again later.`
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
      dispatch(actions.deletingLicenseUserError(error))
    }
  }
}

export const grantNewRoleLicenseUser = (email, licenseId, role) => {
  const grantAdminLicenseUserPayload = {
    email,
    licenseId,
    roles: [role],
    isCssAdmin: true,
  }

  return async dispatch => {
    dispatch(actions.grantingNewRoleLicenseUser())
    try {
      const { data } = await api.put(
        `/licenseuserrole`,
        grantAdminLicenseUserPayload,
      )
      dispatch(
        addNotification({
          color: 'success',
          text: `You've successfully granted the ${role} role to ${email}.`,
        }),
      )
      dispatch(
        actions.grantingNewRoleLicenseUserSuccess(email, licenseId, data.roles),
      )
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: `We weren't able to grant the role ${role} for ${email}. Please try again later.`,
        }),
      )
      dispatch(actions.grantingNewRoleLicenseUserError(error))
    }
  }
}

export const remindLicenseUser = (accessLevel, email, licenseId) => {
  return async dispatch => {
    dispatch(actions.sendingLicenseInvite())
    try {
      if (!licenseId)
        throw new Error('License is undefined, cannot remind user.')

      await api.post('/licenseuserrole/remind', {
        licenseId,
        isCssAdmin: true,
        email,
        roles: [capitalize(accessLevel || '')],
      })
      dispatch(
        addNotification({
          color: 'success',
          text: `You've successfully reminded ${email}.`,
        }),
      )
      dispatch(actions.sendingLicenseInviteSuccess())
    } catch (error) {
      console.error(error)
      dispatch(
        addNotification({
          color: 'danger',
          text: `We weren't able to remind ${email}. Please try again later.`,
        }),
      )
      dispatch(actions.sendingLicenseInviteError(error))
    }
  }
}

export const remindAllLicenseUser = licenseId => {
  return async dispatch => {
    dispatch(actions.sendingLicenseInvite())
    try {
      if (!licenseId)
        throw new Error('License is undefined, cannot remind user.')
      await api.post('/licenseuserrole/remindAll', {
        licenseId,
        isCssAdmin: true,
      })
      dispatch(
        addNotification({
          color: 'success',
          text: `You've successfully reminded all users with pending invites.`,
        }),
      )
      dispatch(actions.sendingLicenseInviteSuccess())
    } catch (error) {
      dispatch(
        addNotification({
          color: 'danger',
          text: `We weren't able to remind some users. Please try again later.`,
        }),
      )
      dispatch(actions.sendingLicenseInviteError(error))
    }
  }
}



// WEBPACK FOOTER //
// ./src/redux/css-admin/licenseManager/operations.js